import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { LEGAL_FORMS, type LegalEntity, type LegalForm } from '../types';
import { db } from '../lib/firebase';
import { COLLECTION_LEGAL_ENTITIES } from '../lib/firestorePaths';

export type LegalEntitiesState = {
  /** Active (non-archived) entities. */
  legalEntities: LegalEntity[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: LegalEntitiesState = {
  legalEntities: [],
  loading: false,
  error: null,
};

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function asLegalForm(value: unknown): LegalForm {
  return LEGAL_FORMS.includes(value as LegalForm) ? (value as LegalForm) : 'Sonstige';
}

function docToLegalEntity(d: QueryDocumentSnapshot): LegalEntity {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  return {
    id: docItemId(d, x),
    userId: typeof x.userId === 'string' ? x.userId : '',
    name: typeof x.name === 'string' && x.name.trim() ? x.name.trim() : 'Entity',
    legalForm: asLegalForm(x.legalForm),
    seat: typeof x.seat === 'string' && x.seat.trim() ? x.seat.trim() : undefined,
    parentEntityId:
      typeof x.parentEntityId === 'string' && x.parentEntityId.trim()
        ? x.parentEntityId.trim()
        : null,
    ownershipBps:
      typeof x.ownershipBps === 'number' && Number.isFinite(x.ownershipBps)
        ? x.ownershipBps
        : null,
    archivedAt: typeof x.archivedAt === 'string' && x.archivedAt ? x.archivedAt : null,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

function toActiveEntities(docs: QueryDocumentSnapshot[]): LegalEntity[] {
  return docs
    .map(docToLegalEntity)
    .filter((entity) => !entity.archivedAt)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function useLegalEntitiesState(userId: string): LegalEntitiesState {
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLegalEntities([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const qItems = query(
      collection(db, COLLECTION_LEGAL_ENTITIES),
      where('userId', '==', userId)
    );

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const itemsSnap = await getDocs(qItems);
        if (cancelled) return;

        setLegalEntities(toActiveEntities(itemsSnap.docs));

        unsubscribers.push(
          onSnapshot(
            qItems,
            (snap) => setLegalEntities(toActiveEntities(snap.docs)),
            (e) => setError(e.message)
          )
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load legal entities');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId]);

  return useMemo(
    () => ({ legalEntities, loading, error }),
    [legalEntities, loading, error]
  );
}

export function emptyLegalEntitiesState(): LegalEntitiesState {
  return EMPTY_STATE;
}
